import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowRight, Plus, Pencil, Trash2, X, BookOpen, ChevronLeft, Upload, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const emptyCourse = {
  title: '', description: '', category: '', level: '', instructor: '',
  image_url: '', is_active: true, order: 0,
};
const emptyBook = {
  title: '', author: '', order: 0, total_pages: 0, pdf_url: '', image_url: '',
};

const LEVEL_OPTIONS = ['مبتدئ', 'متوسط', 'متقدم'];

const AdminCoursesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // selected course_id when managing books
  const [books, setBooks] = useState([]);
  const [booksLoading, setBooksLoading] = useState(false);

  const [courseModal, setCourseModal] = useState({ open: false, editing: null, form: emptyCourse });
  const [bookModal, setBookModal] = useState({ open: false, editing: null, form: emptyBook });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) { navigate('/admin'); return; }
    loadCourses();
  }, []);

  const loadCourses = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/courses');
      setCourses(res.data);
    } catch { toast.error('فشل تحميل المساقات'); }
    finally { setLoading(false); }
  };

  const loadBooks = async (courseId) => {
    setBooksLoading(true);
    try {
      const res = await api.get(`/admin/courses/${courseId}/books`);
      setBooks(res.data);
    } catch { toast.error('فشل تحميل الكتب'); }
    finally { setBooksLoading(false); }
  };

  const openCoursesList = () => { setSelected(null); setBooks([]); };

  const selectCourse = (course) => {
    setSelected(course);
    loadBooks(course.course_id);
  };

  // ===== Course CRUD =====
  const saveCourse = async () => {
    const { editing, form } = courseModal;
    if (!form.title.trim()) { toast.error('العنوان مطلوب'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 0,
      };
      if (editing) {
        await api.put(`/admin/courses/${editing.course_id}`, payload);
        toast.success('تم تحديث المساق');
      } else {
        await api.post('/admin/courses', payload);
        toast.success('تم إنشاء المساق');
      }
      setCourseModal({ open: false, editing: null, form: emptyCourse });
      loadCourses();
    } catch (e) { toast.error(e?.response?.data?.detail || 'فشل الحفظ'); }
    finally { setSaving(false); }
  };

  const deleteCourse = async (c) => {
    if (!window.confirm(`حذف مساق "${c.title}" وجميع كتبه؟`)) return;
    try {
      await api.delete(`/admin/courses/${c.course_id}`);
      toast.success('تم الحذف');
      loadCourses();
    } catch (e) { toast.error(e?.response?.data?.detail || 'فشل الحذف'); }
  };

  // ===== Book CRUD =====
  const saveBook = async () => {
    const { editing, form } = bookModal;
    if (!form.title.trim()) { toast.error('عنوان الكتاب مطلوب'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 0,
        total_pages: Number(form.total_pages) || 0,
      };
      if (editing) {
        await api.put(`/admin/course-books/${editing.book_key}`, payload);
        toast.success('تم تحديث الكتاب');
      } else {
        await api.post(`/admin/courses/${selected.course_id}/books`, payload);
        toast.success('تم إضافة الكتاب');
      }
      setBookModal({ open: false, editing: null, form: emptyBook });
      loadBooks(selected.course_id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'فشل الحفظ'); }
    finally { setSaving(false); }
  };

  const deleteBook = async (b) => {
    if (!window.confirm(`حذف كتاب "${b.title}"؟`)) return;
    try {
      await api.delete(`/admin/course-books/${b.book_key}`);
      toast.success('تم الحذف');
      loadBooks(selected.course_id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'فشل الحذف'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div></div>;

  // ================== BOOKS VIEW ==================
  if (selected) {
    return (
      <div className="space-y-6" data-testid="admin-course-books-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={openCoursesList} data-testid="back-to-courses"><ArrowRight className="w-5 h-5" /></Button>
            <div>
              <h1 className="text-3xl font-bold text-accent">كتب المساق</h1>
              <p className="text-muted-foreground mt-1">{selected.title} · <span className="text-primary font-semibold">{books.length} كتاب</span></p>
            </div>
          </div>
          <Button onClick={() => setBookModal({ open: true, editing: null, form: emptyBook })} className="bg-primary hover:bg-primary/90" data-testid="add-book-btn">
            <Plus className="w-5 h-5 ml-2" /> إضافة كتاب
          </Button>
        </div>

        {booksLoading ? (
          <div className="text-center py-8"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div></div>
        ) : books.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 text-muted mx-auto mb-4 opacity-50" />
            <p className="text-lg text-muted-foreground">لا توجد كتب في هذا المساق بعد</p>
          </div>
        ) : (
          <div className="space-y-3">
            {books.map((b) => (
              <Card key={b.book_key} className="p-5 flex items-center gap-4 hover:shadow-md transition-shadow" data-testid={`book-${b.book_key}`}>
                <span className="w-10 h-10 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center shrink-0">{b.order || '-'}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-accent truncate">{b.title}</p>
                  <p className="text-sm text-muted-foreground">
                    {b.author || '—'} · {b.total_pages} صفحة · <span dir="ltr" className="text-xs">{b.book_key}</span>
                  </p>
                  {b.pdf_url && <a href={b.pdf_url} target="_blank" rel="noreferrer" className="text-xs text-primary underline">رابط PDF</a>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="icon" onClick={() => setBookModal({ open: true, editing: b, form: { ...emptyBook, ...b } })} data-testid={`edit-book-${b.book_key}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="outline" size="icon" className="text-red-500" onClick={() => deleteBook(b)} data-testid={`delete-book-${b.book_key}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {bookModal.open && (
          <FormModal
            title={bookModal.editing ? 'تعديل كتاب' : 'إضافة كتاب جديد'}
            onClose={() => setBookModal({ open: false, editing: null, form: emptyBook })}
            onSave={saveBook}
            saving={saving}
            fields={[
              { key: 'title', label: 'عنوان الكتاب *', required: true, testid: 'book-title' },
              { key: 'author', label: 'المؤلف', testid: 'book-author' },
              { key: 'order', label: 'الترتيب', type: 'number', testid: 'book-order' },
              { key: 'total_pages', label: 'عدد الصفحات', type: 'number', testid: 'book-pages' },
              { key: 'pdf_url', label: 'رابط PDF', dir: 'ltr', testid: 'book-pdf' },
              { key: 'image_url', label: 'صورة الكتاب', image: true, testid: 'book-image' },
            ]}
            form={bookModal.form}
            setForm={(newForm) => setBookModal({ ...bookModal, form: newForm })}
          />
        )}
      </div>
    );
  }

  // ================== COURSES VIEW ==================
  return (
    <div className="space-y-6" data-testid="admin-courses-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/admin')}><ArrowRight className="w-5 h-5" /></Button>
          <div>
            <h1 className="text-4xl font-bold text-accent">إدارة المساقات</h1>
            <p className="text-muted-foreground text-lg mt-1">{courses.length} مساق</p>
          </div>
        </div>
        <Button onClick={() => setCourseModal({ open: true, editing: null, form: emptyCourse })} className="bg-primary hover:bg-primary/90" data-testid="add-course-btn">
          <Plus className="w-5 h-5 ml-2" /> إضافة مساق
        </Button>
      </div>

      {courses.length === 0 ? (
        <div className="text-center py-16">
          <BookOpen className="w-16 h-16 text-muted mx-auto mb-4 opacity-50" />
          <p className="text-lg text-muted-foreground">لا توجد مساقات بعد</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((c) => (
            <Card key={c.course_id} className="p-5 hover:shadow-md transition-shadow flex flex-col" data-testid={`course-${c.course_id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-lg text-accent truncate">{c.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.category || '—'} · {c.level || '—'}</p>
                </div>
                {!c.is_active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-600">غير نشط</span>}
              </div>
              {c.description && <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{c.description}</p>}
              <div className="flex items-center justify-between text-sm mt-auto pt-3 border-t">
                <span className="text-primary font-semibold">{c.books_count || 0} كتاب</span>
                <span className="text-xs text-muted-foreground">ترتيب: {c.order || 0}</span>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" className="flex-1 bg-primary hover:bg-primary/90" onClick={() => selectCourse(c)} data-testid={`manage-books-${c.course_id}`}>
                  <BookOpen className="w-4 h-4 ml-1" /> الكتب <ChevronLeft className="w-4 h-4 mr-1" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => setCourseModal({ open: true, editing: c, form: { ...emptyCourse, ...c } })} data-testid={`edit-course-${c.course_id}`}><Pencil className="w-4 h-4" /></Button>
                <Button variant="outline" size="icon" className="text-red-500" onClick={() => deleteCourse(c)} data-testid={`delete-course-${c.course_id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {courseModal.open && (
        <FormModal
          title={courseModal.editing ? 'تعديل مساق' : 'إضافة مساق جديد'}
          onClose={() => setCourseModal({ open: false, editing: null, form: emptyCourse })}
          onSave={saveCourse}
          saving={saving}
          fields={[
            { key: 'title', label: 'اسم المساق *', required: true, testid: 'course-title' },
            { key: 'description', label: 'الوصف', textarea: true, testid: 'course-desc' },
            { key: 'category', label: 'التصنيف', testid: 'course-category' },
            { key: 'level', label: 'المرحلة', select: LEVEL_OPTIONS, testid: 'course-level' },
            { key: 'instructor', label: 'المدرّس', testid: 'course-instructor' },
            { key: 'image_url', label: 'صورة المساق', image: true, testid: 'course-image' },
            { key: 'order', label: 'الترتيب', type: 'number', testid: 'course-order' },
            { key: 'is_active', label: 'نشط', checkbox: true, testid: 'course-active' },
          ]}
          form={courseModal.form}
          setForm={(newForm) => setCourseModal({ ...courseModal, form: newForm })}
        />
      )}
    </div>
  );
};

// Reusable modal
const FormModal = ({ title, onClose, onSave, saving, fields, form, setForm }) => {
  const [uploading, setUploading] = useState({});

  const handleImageUpload = async (key, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('يرجى اختيار صورة فقط');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('حجم الصورة أكبر من 5 ميجابايت');
      return;
    }
    setUploading((p) => ({ ...p, [key]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload/image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const absoluteUrl = `${API_BASE}/api/files/${res.data.path}`;
      setForm({ ...form, [key]: absoluteUrl });
      toast.success('تم رفع الصورة');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل رفع الصورة');
    } finally {
      setUploading((p) => ({ ...p, [key]: false }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-accent">{title}</h2>
          <button onClick={onClose} data-testid="modal-close"><X className="w-6 h-6 text-muted-foreground" /></button>
        </div>
        <div className="space-y-4">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="block text-sm font-medium mb-1">{f.label}</label>
              {f.checkbox ? (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} data-testid={f.testid} className="w-4 h-4" />
                  <span className="text-sm text-muted-foreground">{form[f.key] ? 'نعم' : 'لا'}</span>
                </label>
              ) : f.select ? (
                <select value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="w-full border-2 rounded-xl p-3 focus:outline-none focus:border-primary" data-testid={f.testid}>
                  <option value="">—</option>
                  {f.select.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              ) : f.textarea ? (
                <textarea value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="w-full border-2 rounded-xl p-3 min-h-[80px] focus:outline-none focus:border-primary" dir={f.dir || 'rtl'} data-testid={f.testid} />
              ) : f.image ? (
                <div className="space-y-2">
                  {form[f.key] ? (
                    <div className="relative inline-block">
                      <img src={form[f.key]} alt="" className="w-32 h-32 object-cover rounded-xl border-2 border-primary/20" data-testid={`${f.testid}-preview`} />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, [f.key]: '' })}
                        className="absolute top-1 left-1 bg-white/95 hover:bg-rose-50 rounded-full p-1 shadow"
                        data-testid={`${f.testid}-remove`}
                      ><X className="w-4 h-4 text-rose-600" /></button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 w-32 h-32 border-2 border-dashed border-primary/30 rounded-xl cursor-pointer hover:bg-primary/5 transition" data-testid={`${f.testid}-upload`}>
                      {uploading[f.key] ? (
                        <div className="animate-spin h-6 w-6 border-2 border-primary rounded-full border-t-transparent" />
                      ) : (
                        <>
                          <ImageIcon className="w-6 h-6 text-primary" />
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(f.key, e.target.files[0])} />
                        </>
                      )}
                    </label>
                  )}
                  <Input
                    type="text"
                    value={form[f.key] || ''}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    dir="ltr"
                    placeholder="أو ألصق رابط صورة مباشر"
                    className="text-xs"
                    data-testid={f.testid}
                  />
                </div>
              ) : (
                <Input type={f.type || 'text'} value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} dir={f.dir || 'rtl'} data-testid={f.testid} />
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-3 mt-6">
          <Button onClick={onSave} disabled={saving} className="flex-1 bg-primary hover:bg-primary/90" data-testid="save-btn">
            {saving ? 'جارٍ الحفظ...' : 'حفظ'}
          </Button>
          <Button variant="outline" onClick={onClose} className="flex-1">إلغاء</Button>
        </div>
      </div>
    </div>
  );
};

export default AdminCoursesPage;
